import React, { useState, useEffect, useRef } from 'react';
import {
  useBreadcrumbRoutes,
  useUpdate,
  Wrapper,
  EllipsisSpan,
  usePaging,
  FormTitle,
  Button,
  Radio,
  Table,
  Tag,
  Paging,
  Tooltip,
} from '@maxtropy/components';
import { CheckCircleFilled, DoubleRightOutlined, LeftOutlined, RightOutlined, WarningFilled } from '@ant-design/icons';
import { Layout } from 'antd';
import StationTree from './components/StationTree';
import styles from './index.module.scss';
import { useNavigate } from 'react-router';
import {
  apiV2PowerRoomConfigCircuitAlarmListWithRoomIdPost,
  apiV2PowerRoomConfigCircuitPagePost,
} from '@maxtropy/device-customer-apis-v2';
import { isNil } from 'lodash-es';
import DragResize from '@/components/DragResize';
import { AlarmItem, CircuitTableItem, DisplayMode } from './utils';
import RightContent from './components/RightContent';
import MaintenanceDrawer from './components/MaintenanceDrawer';
import dayjs from 'dayjs';
import CircuitDataModal from './components/CircuitDataModal';
import { keepTwoNull, useHasPermission } from '@/utils/utils';
import WiringDiagramHighV from './components/WiringDiagramHighV';
import { PermissionsType } from '@/common/permissionsConst';

const StationConfiguration: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [updateState, forceUpdate] = useUpdate();
  const pagingInfo = usePaging(20);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [circuitList, setCircuitList] = useState<CircuitTableItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCircuitModal, setShowCircuitModal] = useState<boolean>(false);
  const [displayMode, setDisplayMode] = useState<DisplayMode>(DisplayMode.Graph);
  const hasOperationPermission = useHasPermission(PermissionsType.B_OPERATIONMAINTENANCEINFO);

  const navigate = useNavigate();

  const [roomId, setRoomId] = useState<number>();
  const [roomName, setRoomName] = useState<string>();
  const [isConfigurationMonitoring, setIsConfigurationMonitoring] = useState<boolean>();

  const [circuitId, setCircuitId] = useState<number>();
  const [circuitName, setCircuitName] = useState<string>();
  const [siderWidth, setSiderWidth] = useState(260);
  const [openSider, setOpenSider] = useState(true);
  const [dragStatus, setDragStatus] = useState(false);

  const [alarmList, setAlarmList] = useState<AlarmItem[]>([]);
  const [currentAlarmIndex, setCurrentAlarmIndex] = useState<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [showDrawer, setShowDrawer] = useState<boolean>(false);

  const updateCircuitListStatus = (oldList: CircuitTableItem[], newList: CircuitTableItem[]): CircuitTableItem[] => {
    const map = new Map(newList.map(item => [item.circuitId, item]));
    return oldList.map(item => {
      const updated = map.get(item.circuitId);
      if (updated) {
        return {
          ...item,
          isAlarm: updated.isAlarm,
          electricCurrentA: updated.electricCurrentA,
          electricCurrentB: updated.electricCurrentB,
          electricCurrentC: updated.electricCurrentC,
          voltageAb: updated.voltageAb,
          voltageBc: updated.voltageBc,
          voltageCa: updated.voltageCa,
          dailyElectricity: updated.dailyElectricity,
        };
      }
      return item;
    });
  };

  // 初始化加载回路列表
  useEffect(() => {
    if (isNil(roomId)) return;
    setIsLoading(true);
    setCircuitList([]);
    apiV2PowerRoomConfigCircuitPagePost({
      page: pageOffset,
      size: pageSize,
      roomId: Number(roomId),
    })
      .then((res: any) => {
        setCircuitList(res?.list ?? []);
        setTotalCount(res?.total);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [pageOffset, pageSize, setTotalCount, updateState, roomId]);

  useEffect(() => {
    if (isNil(roomId) || displayMode !== DisplayMode.Table) return;

    const interval = setInterval(() => {
      apiV2PowerRoomConfigCircuitPagePost({
        page: pageOffset,
        size: pageSize,
        roomId: Number(roomId),
      })
        .then((res: any) => {
          const updated = updateCircuitListStatus(circuitList, res?.list ?? []);
          setCircuitList(updated);
        })
        .catch(console.error);
    }, 5000);

    return () => clearInterval(interval);
  }, [roomId, pageOffset, pageSize, circuitList, displayMode]);

  // 获取报警列表
  useEffect(() => {
    if (isNil(roomId)) return;

    const fetchAlarmList = () => {
      apiV2PowerRoomConfigCircuitAlarmListWithRoomIdPost({ id: roomId }).then(res => {
        setAlarmList(res?.list ?? []);
        setCurrentAlarmIndex(0);
      });
    };

    fetchAlarmList(); // 立即执行一次

    const interval = setInterval(fetchAlarmList, 20000);
    return () => clearInterval(interval);
  }, [roomId]);

  // 设置报警轮播
  useEffect(() => {
    if (timerRef.current) {
      setCurrentAlarmIndex(0);
      clearInterval(timerRef.current);
    }
    if (alarmList.length > 0) {
      timerRef.current = setInterval(() => {
        setCurrentAlarmIndex(prevIndex => (prevIndex + 1) % alarmList.length);
      }, 5000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [alarmList]);

  const currentAlarm = alarmList.length > 0 ? alarmList[currentAlarmIndex] : null;

  const columns = [
    {
      title: '回路名称',
      dataIndex: 'circuitName',
      width: 340,
      fixed: 'left' as 'left',
      render: (v: string, record: any) => (
        <div style={{ width: '100%' }}>
          <Button
            type="link"
            onClick={() => {
              setCircuitId(record.circuitId);
              setCircuitName(v);
              setShowCircuitModal(true);
            }}
            style={{ padding: 0 }}
          >
            <Tooltip title={v}>
              <div className={styles.line_clamp_2}>{v}</div>
            </Tooltip>
            {record.isAlarm ? (
              <WarningFilled style={{ marginLeft: 4, color: '#FAAD14', verticalAlign: 'middle' }} />
            ) : null}
          </Button>
        </div>
      ),
    },
    {
      title: '配电柜',
      dataIndex: 'distributionCabinetName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '电流(A)',
      dataIndex: 'electricCurrentA',
      render: (v: string, record: any) => (
        <div className={styles.cell}>
          <Tooltip title={keepTwoNull(record.electricCurrentA)}>
            <span>{keepTwoNull(record.electricCurrentA)}</span>
          </Tooltip>
          <Tooltip title={keepTwoNull(record.electricCurrentB)}>
            <span>{keepTwoNull(record.electricCurrentB)}</span>
          </Tooltip>
          <Tooltip title={keepTwoNull(record.electricCurrentC)}>
            <span>{keepTwoNull(record.electricCurrentC)}</span>
          </Tooltip>
        </div>
      ),
    },
    {
      title: '线电压(V)',
      dataIndex: 'voltageBc',
      ellipsis: { showTitle: true },
      render: (v: string, record: any) => (
        <div className={styles.cell}>
          <Tooltip title={keepTwoNull(record.voltageAb)}>
            <span>{keepTwoNull(record.voltageAb)}</span>
          </Tooltip>
          <Tooltip title={keepTwoNull(record.voltageBc)}>
            <span>{keepTwoNull(record.voltageBc)}</span>
          </Tooltip>
          <Tooltip title={keepTwoNull(record.voltageCa)}>
            <span>{keepTwoNull(record.voltageCa)}</span>
          </Tooltip>
        </div>
      ),
    },
    {
      title: '当日用电(kWh)',
      dataIndex: 'dailyElectricity',
      ellipsis: { showTitle: true },
      render: (v: number) => <EllipsisSpan value={keepTwoNull(v)} />,
    },
  ];

  const dragChange = (width: number) => {
    if (openSider) {
      setSiderWidth(width);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <Wrapper routes={breadcrumbRoutes?.routes ?? []} hasPadding={false} className={styles.wrapper}>
        <Layout className={styles.layout}>
          <Layout.Sider
            width={openSider ? siderWidth : 0}
            style={{ transition: dragStatus ? 'none' : 'all 0.2s', background: 'var(--mx-module-bg-color)' }}
          >
            <DragResize init={260} dragChange={dragChange} dragStatus={setDragStatus} />
            <StationTree
              setRoomId={setRoomId}
              setRoomName={setRoomName}
              setIsConfigurationMonitoring={setIsConfigurationMonitoring}
            />
          </Layout.Sider>
          <Layout.Content className={styles.content}>
            <div className="toggleBtn" style={{ left: openSider ? -30 : 0 }} onClick={() => setOpenSider(!openSider)}>
              {openSider ? <LeftOutlined /> : <RightOutlined />}
            </div>

            <div className={styles.container}>
              <div className={styles.left}>
                <FormTitle
                  title={roomName}
                  extraContent={
                    hasOperationPermission && (
                      <Button type="primary" onClick={() => setShowDrawer(true)}>
                        运维信息
                      </Button>
                    )
                  }
                />
                <div className={styles.middleRow}>
                  <Radio.Group
                    buttonStyle="solid"
                    onChange={v => {
                      setDisplayMode(v.target.value);
                    }}
                    value={displayMode}
                  >
                    <Radio.Button value={DisplayMode.Graph}>一次接线图</Radio.Button>
                    <Radio.Button value={DisplayMode.Table}>列表展示</Radio.Button>
                  </Radio.Group>
                  {alarmList.length ? (
                    <Tag border="solid" color="warning" icon={<WarningFilled />}>
                      <div className={styles.tagContent}>
                        <div className={styles.alarmMessage}>
                          {`${currentAlarm?.circuitName} ${currentAlarm?.alarmInfo}`}
                        </div>
                        <div className={styles.time}>{dayjs(currentAlarm?.alarmTime).format('MM-DD HH:mm')}</div>
                        <Button
                          type="link"
                          style={{ padding: 0 }}
                          onClick={() => {
                            if (isNil(currentAlarm?.alarmId)) return;
                            window.open(`/device/alarm/record/detail/${currentAlarm?.alarmId}`, '_blank');
                          }}
                        >
                          详情
                          <DoubleRightOutlined />
                        </Button>
                      </div>
                    </Tag>
                  ) : (
                    <Tag border="solid" color="success" icon={<CheckCircleFilled />}>
                      暂无报警
                    </Tag>
                  )}
                </div>
                <div className={styles.bottomContent}>
                  {displayMode === DisplayMode.Table ? (
                    <>
                      <Table
                        sticky
                        scroll={{ x: 1300 }}
                        loading={isLoading}
                        rowKey="id"
                        columns={columns}
                        dataSource={circuitList}
                      />
                      <Paging pagingInfo={pagingInfo} />
                    </>
                  ) : (
                    <div style={{ width: '100%', height: '100%' }}>
                      <WiringDiagramHighV roomId={roomId} />
                    </div>
                  )}
                </div>
              </div>
              <RightContent roomId={roomId} isConfigurationMonitoring={isConfigurationMonitoring} />
            </div>
          </Layout.Content>
        </Layout>
      </Wrapper>
      {showDrawer && <MaintenanceDrawer roomId={roomId} show={showDrawer} close={() => setShowDrawer(false)} />}
      {showCircuitModal && (
        <CircuitDataModal
          visible={showCircuitModal}
          onCancel={() => setShowCircuitModal(false)}
          circuitId={circuitId!}
          circuitName={circuitName!}
        />
      )}
    </div>
  );
};

export default StationConfiguration;
